import React, { useState, useEffect, useMemo } from "react";
import APLPresenceWeeksView from "./APLPresenceWeeksView";
import { GetAttendanceStatuses } from "../../actions/teacherQueries";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import { Col, Row, Card, CardBody, Tooltip, Button } from "reactstrap";
import APLPresenceColorInfo from "./APLPresenceColorInfo";
import { FormattedMessage } from "react-intl";
import FoodCompensationInformationForStudentModal from "../Modal/FoodCompensationInformationForStudentModal";
import { getWeek, getYear } from "date-fns";
import { DatePicker } from "react-nice-dates";
import { sv } from "date-fns/locale";
import CheckInMapForAll from "./CheckInMapForAll";
import StatusIndicator from "./shared/StatusIndicator";

export default function APLPresence({
  students,
  objId,
  level,
  activePeriodId,
}) {
  const attendanceByWeek = GetAttendanceStatuses(level, activePeriodId, objId);
  const [attendingTooltipIsOpen, setAttendingTooltipIsOpen] = useState(false);
  const [notAttendingTooltipIsOpen, setNotAttendingTooltipIsOpen] =
    useState(false);
  const [studentForFoodCompensation, setStudentForFoodCompensation] =
    useState(null);
  const [selectedDateForPosition, setSelectedDateForPosition] = useState(null);
  const [markersWithPosition, setMarkersWithPosition] = useState(null);

  const { attendingStudents, notAttendingStudents } = useMemo(() => {
    if (attendanceByWeek.isLoading) {
      return { attendingStudents: [], notAttendingStudents: [] };
    }

    const attending = [];
    const notAttending = [];

    students.forEach((stud) => {
      const studentStatus = attendanceByWeek.data.checkedInStatus.find(
        (statusStud) => statusStud.studentId === stud.studentId
      );
      if (studentStatus.isCheckedIn) {
        attending.push({
          studentId: studentStatus.studentId,
          name: stud.student.name,
        });
      } else {
        notAttending.push({
          studentId: studentStatus.studentId,
          name: stud.student.name,
        });
      }
    });
    return { attendingStudents: attending, notAttendingStudents: notAttending };
  }, [attendanceByWeek.isLoading, attendanceByWeek.data, students]);

  useEffect(() => {
    if (!attendanceByWeek.isLoading && selectedDateForPosition != null) {
      const selectedWeek = getWeek(selectedDateForPosition, {
        weekStartsOn: 1,
        firstWeekContainsDate: 4,
        locale: sv,
      });
      const selectedYear = getYear(selectedDateForPosition);
      const attendancesForTheWeek = attendanceByWeek.data.weeks.find(
        (w) => w.weekNumber === selectedWeek && w.year === selectedYear
      );
      if (attendancesForTheWeek != null) {
        const attendancesForTheDay =
          attendancesForTheWeek.attendanceForStudents.filter(
            (studAtt) =>
              new Date(studAtt.day).getTime() ===
                selectedDateForPosition.getTime() &&
              studAtt.attendanceThisDay != null
          );
        const lastPoints = attendancesForTheDay
          .map((item) => {
            const points = item.attendanceThisDay?.points ?? [];

            const filteredPoints = points.filter(
              (p) => p.latitude != null && p.longitude != null
            );

            if (filteredPoints.length === 0) return null;

            const lastPoint = filteredPoints.reduce((latest, current) =>
              new Date(current.time) > new Date(latest.time) ? current : latest
            );
            return {
              studentId: item.attendanceThisDay.studentId,
              studentName: item.attendanceThisDay.studentName,
              lastPoint,
            };
          })
          .filter((p) => p !== null);
        if (lastPoints.length > 0) {
          const tempMarkerpoints = [];
          lastPoints.forEach((point) => {
            const markerPoint = {
              studentName: point.studentName,
              studentId: point.studentId,
              position: {
                lat: point.lastPoint.latitude,
                lng: point.lastPoint.longitude,
              },
              time: point.lastPoint.time,
            };
            if (point.lastPoint.latitude && point.lastPoint.longitude) {
              tempMarkerpoints.push(markerPoint);
            }
          });
          setMarkersWithPosition(tempMarkerpoints);
        }
      }
    }
  }, [
    attendanceByWeek.isLoading,
    attendanceByWeek.data,
    selectedDateForPosition,
  ]);

  const toggleAttendingToolTip = () =>
    setAttendingTooltipIsOpen(!attendingTooltipIsOpen);
  const toggleNotAttendingToolTip = () =>
    setNotAttendingTooltipIsOpen(!notAttendingTooltipIsOpen);

  if (attendanceByWeek.isLoading) {
    return <Pacmanloader />;
  }

  return (
    <div>
      <Row style={{ paddingInline: "14px" }}>
        <Col className="text-nowrap mb-3" style={{ flexGrow: "0" }}>
          <span id={"Tooltip-Attending"}>
            <span className="me-1" style={{ verticalAlign: "middle" }}>
              <StatusIndicator status="checkedIn" />
            </span>

            <span>
              {attendingStudents.length} <FormattedMessage id="CheckedIn" />
            </span>
            <Tooltip
              placement="bottom"
              isOpen={attendingTooltipIsOpen}
              target={"Tooltip-Attending"}
              toggle={toggleAttendingToolTip}
            >
              {attendingStudents.map((stud) => {
                return (
                  <Row key={stud.studentId}>
                    <Col>{stud.name}</Col>
                  </Row>
                );
              })}
            </Tooltip>
          </span>

          <span className="ms-3" id={"Tooltip-NotAttending"}>
            <span className="me-1" style={{ verticalAlign: "middle" }}>
              <StatusIndicator status="notCheckedIn" />
            </span>
            <span>
              {notAttendingStudents.length}{" "}
              <FormattedMessage id="NotCheckedIn" />
            </span>
            <Tooltip
              placement="bottom"
              isOpen={notAttendingTooltipIsOpen}
              target={"Tooltip-NotAttending"}
              toggle={toggleNotAttendingToolTip}
            >
              {notAttendingStudents.map((stud) => {
                return (
                  <Row key={stud.studentId}>
                    <Col>{stud.name}</Col>
                  </Row>
                );
              })}
            </Tooltip>
          </span>

          <APLPresenceColorInfo />
        </Col>

        <Col
          className="mb-3 p-2 border rounded align-content-center"
          style={{
            flexGrow: "0",
            justifyItems: "center",
            minWidth: "226px",
            marginLeft: "9px",
          }}
        >
          <Row className="mx-0">
            <span className="lead text-center">
              <FormattedMessage id="SelectDateToSeeLatestCheckinsForAllStudents" />
            </span>
            <span className="text-muted text-center">
              (<FormattedMessage id="OnlyCheckinsThatHasALocationIsShown" />)
            </span>
          </Row>
          <Row className="mx-0 mt-2 datepicker-show-map-pins-container">
            <DatePicker
              name="date"
              date={selectedDateForPosition}
              onDateChange={(value) => setSelectedDateForPosition(value)}
              locale={sv}
              format="yyyy-MM-dd"
            >
              {({ inputProps, focused }) => (
                <input
                  className={
                    "text-center input" +
                    (focused ? " -focused " : " ") +
                    "date--picker"
                  }
                  {...inputProps}
                />
              )}
            </DatePicker>
          </Row>
        </Col>
      </Row>

      {students.map((student) => {
        const studentCheckedIn = attendanceByWeek.data.checkedInStatus.find(
          (studStatus) => studStatus.studentId === student.studentId
        ).isCheckedIn;
        return (
          <Card className="m-2" key={student.studentId}>
            <Row className="p-2">
              <Col>
                {studentCheckedIn ? (
                  <StatusIndicator status="checkedIn" />
                ) : (
                  <StatusIndicator status="notCheckedIn" />
                )}

                <span className="p-2 mb-0 lead">
                  {student.student.name}{" "}
                  <small style={{ fontWeight: "300" }}>
                    <FormattedMessage
                      id={
                        studentCheckedIn
                          ? "IsCurrentlyCheckedInOnThisPeriod"
                          : "IsNotCurrentlyCheckedInOnThisPeriod"
                      }
                    />
                  </small>
                </span>
              </Col>
            </Row>
            <CardBody className="pt-0">
              <Row className="pb-2">
                <Col className="ps-4">
                  <Button
                    onClick={() =>
                      setStudentForFoodCompensation({
                        name: student.student.name,
                        id: student.studentId,
                      })
                    }
                    size="sm"
                    outline
                    color="primary"
                  >
                    <FormattedMessage id="ShowFoodCompensation" />
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col>
                  {!attendanceByWeek.isLoading && (
                    <APLPresenceWeeksView
                      attendanceByWeek={attendanceByWeek}
                      studentId={student.studentId}
                      objId={objId}
                      level={level}
                      activePeriodId={activePeriodId}
                    />
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        );
      })}
      {studentForFoodCompensation !== null && (
        <FoodCompensationInformationForStudentModal
          close={() => setStudentForFoodCompensation(null)}
          isOpen={studentForFoodCompensation !== null}
          student={studentForFoodCompensation}
          periodId={activePeriodId}
        />
      )}
      {markersWithPosition != null && (
        <CheckInMapForAll
          isOpen={markersWithPosition != null}
          toggle={() => setMarkersWithPosition(null)}
          markerPoints={markersWithPosition}
        />
      )}
    </div>
  );
}
